import { Accordion } from '@delmonicos/shared-design-system-react/src/components/accordion/accordion';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { Dialog } from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  Icon,
  IconColor,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { MiniTitle } from '@delmonicos/shared-design-system-react/src/components/typography/mini-title/mini-title';
import { Text } from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { formatDate } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/format-date';
import { hasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import i18n from '@app/core/locale/i18n';

import { EmspBoEmspUserOcpiTokenDeleteController } from '../emsp-bo-emsp-user-ocpi-token-delete/emsp-bo-emsp-user-ocpi-token-delete.controller';
import { EmspBoEmspUserOcpiTokenUpdateController } from '../emsp-bo-emsp-user-ocpi-token-update/emsp-bo-emsp-user-ocpi-token-update.controller';
import { useEmspBoEmspUserOcpiTokenContext } from '../emsp-bo-emsp-user.context';

const AccordionTitleUid = ({
  ocpiUid,
}: {
  ocpiUid: string;
}): React.JSX.Element => {
  const { t } = useTranslation();
  return (
    <Stack gap={GapEnum.S}>
      <MiniTitle>
        {t('delmonicos-shared-sdk.outputs-emsp-user-ocpi-token.ocpi-uid')}
      </MiniTitle>
      <Text>{ocpiUid}</Text>
    </Stack>
  );
};

export const EmspBoEmspUserOcpiTokenDetailsController =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    const { t } = useTranslation();
    const ocpiToken = useEmspBoEmspUserOcpiTokenContext();

    // USER PERMISSIONS //////////////////////////////////////////////////////////

    const userPermissions = useAppSelector(permissions);

    const canDelete = useMemo(
      () =>
        userPermissions &&
        hasAccessTo(userPermissions, {
          resourceName: Resource.EMSP_BO,
          action: ActionsByResource.EMSP_BO.DELETE_EMSP_USER_OCPI_TOKEN,
        }),
      [userPermissions],
    );

    const canUpdate = useMemo(
      () =>
        userPermissions &&
        hasAccessTo(userPermissions, {
          resourceName: Resource.EMSP_BO,
          action: ActionsByResource.EMSP_BO.UPDATE_EMSP_USER_OCPI_TOKEN,
        }),
      [userPermissions],
    );

    // ACTIONS /////////////////////////////////////////////////////////////////

    const [actionDeleteIsDialogOpen, setActionDeleteIsDialogOpen] =
      useState(false);

    const [actionUpdateIsDialogOpen, setActionUpdateIsDialogOpen] =
      useState(false);

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <>
        <Accordion
          isOpen={false}
          title={<AccordionTitleUid ocpiUid={ocpiToken.ocpiUid} />}>
          <Stack direction="column" gap={GapEnum.M}>
            <Stack direction="column" gap={GapEnum.XS}>
              <MiniTitle>
                {t(
                  'delmonicos-shared-sdk.outputs-emsp-user-ocpi-token.ocpi-uid',
                )}
              </MiniTitle>
              <Text>{ocpiToken.ocpiUid}</Text>
            </Stack>
            <Stack direction="column" gap={GapEnum.XS}>
              <MiniTitle>
                {t(
                  'delmonicos-shared-sdk.outputs-emsp-user-ocpi-token.ocpi-contract-id',
                )}
              </MiniTitle>
              <Text>{ocpiToken.ocpiContractId}</Text>
            </Stack>
            <Stack direction="column" gap={GapEnum.XS}>
              <MiniTitle>
                {t('delmonicos-shared-sdk.outputs-emsp-user-ocpi-token.type')}
              </MiniTitle>
              <Text>{ocpiToken.type}</Text>
            </Stack>
            <Stack direction="column" gap={GapEnum.XS}>
              <MiniTitle>
                {t(
                  'delmonicos-shared-sdk.outputs-emsp-user-ocpi-token.language',
                )}
              </MiniTitle>
              <Text>{ocpiToken.language}</Text>
            </Stack>
            <Stack direction="column" gap={GapEnum.XS}>
              <MiniTitle>
                {t(
                  'delmonicos-shared-sdk.outputs-emsp-user-ocpi-token.is-activated',
                )}
              </MiniTitle>
              <Stack alignItems="center" gap={GapEnum.XS}>
                {ocpiToken.isActivated ? (
                  <Icon name="check-circle" color={IconColor.Success} />
                ) : (
                  <Icon name="x-circle" color={IconColor.Error} />
                )}
              </Stack>
            </Stack>
            <Stack direction="column" gap={GapEnum.XS}>
              <MiniTitle>
                {t(
                  'delmonicos-shared-sdk.outputs-emsp-user-ocpi-token.is-revoked',
                )}
              </MiniTitle>
              <Stack alignItems="center" gap={GapEnum.XS}>
                {ocpiToken.isRevoked ? (
                  <Icon name="x-circle" color={IconColor.Error} />
                ) : (
                  <Icon name="check-circle" color={IconColor.Success} />
                )}
              </Stack>
            </Stack>
            <Stack direction="column" gap={GapEnum.XS}>
              <MiniTitle>
                {t(
                  'delmonicos-shared-sdk.outputs-emsp-user-ocpi-token.created-at',
                )}
              </MiniTitle>
              <Text>{formatDate(ocpiToken.createdAt, i18n)}</Text>
            </Stack>
            <Stack direction="column" gap={GapEnum.XS}>
              <MiniTitle>
                {t(
                  'delmonicos-shared-sdk.outputs-emsp-user-ocpi-token.updated-at',
                )}
              </MiniTitle>
              <Text>{formatDate(ocpiToken.updatedAt, i18n)}</Text>
            </Stack>
            <Stack gap={GapEnum.M}>
              {canUpdate && (
                <ButtonIcon
                  icon="edit-05"
                  onClick={() => setActionUpdateIsDialogOpen(true)}
                />
              )}
              {canDelete && (
                <ButtonIcon
                  icon="trash-01"
                  onClick={() => setActionDeleteIsDialogOpen(true)}
                />
              )}
            </Stack>
          </Stack>
        </Accordion>
        <Dialog
          open={actionDeleteIsDialogOpen}
          onClose={() => setActionDeleteIsDialogOpen(false)}>
          <Dialog.Header
            title={t('$.emsp-bo-emsp-user-ocpi-token-delete.title')}
            icon="credit-card-01"
            onClose={() => setActionDeleteIsDialogOpen(false)}
          />
          <Dialog.Content>
            <EmspBoEmspUserOcpiTokenDeleteController
              onCancel={() => setActionDeleteIsDialogOpen(false)}
              onSuccess={() => setActionDeleteIsDialogOpen(false)}
            />
          </Dialog.Content>
        </Dialog>
        <Dialog
          open={actionUpdateIsDialogOpen}
          onClose={() => setActionUpdateIsDialogOpen(false)}>
          <Dialog.Header
            title={t('$.emsp-bo-emsp-user-ocpi-token-update.title')}
            icon="credit-card-01"
            onClose={() => setActionUpdateIsDialogOpen(false)}
          />
          <Dialog.Content>
            <EmspBoEmspUserOcpiTokenUpdateController
              onCancel={() => setActionUpdateIsDialogOpen(false)}
              onSuccess={() => setActionUpdateIsDialogOpen(false)}
            />
          </Dialog.Content>
        </Dialog>
      </>
    );
  };
